/**
 * DocumentPreviewModal — P0.18.2.2 Operational Document Hub Completion (Aug 2026).
 *
 * Inline preview van een document. Ondersteunt:
 *   • PDF → <iframe> met blob URL
 *   • XLSX/CSV → download-prompt met veilige melding
 *   • JSON/XML → syntax-highlighted <pre> weergave
 *
 * Fetcht via de secure ``/admin/document-hub/{id}/preview`` endpoint zodat
 * we altijd auth + RBAC toepassen. Blob wordt in memory gehouden en bij
 * unmount opgeschoond.
 *
 * data-testids:
 *   document-preview-modal
 *   document-preview-close
 *   document-preview-pdf-iframe
 *   document-preview-download-btn
 *   document-preview-loading
 *   document-preview-error
 */
import React, { useEffect, useState } from "react";
import { X, Download, Loader2, FileText, AlertTriangle } from "lucide-react";
import { adminApi, formatApiError } from "../../../context/AuthContext";

export default function DocumentPreviewModal({ document: doc, onClose }) {
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setContentType] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [filename, setFilename] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const resp = await adminApi.get(
          `/admin/document-hub/${encodeURIComponent(doc.id)}/preview`,
          { responseType: "blob" }
        );
        if (cancelled) return;
        const ctype = resp.headers["content-type"] || "application/pdf";
        setContentType(ctype);
        const disp = resp.headers["content-disposition"] || "";
        const m = disp.match(/filename="?([^";]+)"?/);
        setFilename(m ? m[1] : doc.title || doc.id);

        // Als tekstueel (JSON/XML/CSV), read as text voor pretty display
        if (ctype.startsWith("application/json")
            || ctype.startsWith("application/xml")
            || ctype.startsWith("text/csv")) {
          const text = await resp.data.text();
          setTextContent(text);
        } else {
          const url = window.URL.createObjectURL(resp.data);
          createdUrl = url;
          setBlobUrl(url);
        }
      } catch (err) {
        setError(formatApiError(err) || "Preview kon niet worden geladen");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) window.URL.revokeObjectURL(createdUrl);
    };
  }, [doc.id]);

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const isPdf = contentType && contentType.includes("application/pdf");
  const isXlsx = contentType && contentType.includes("spreadsheetml");
  const isText = textContent !== null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      data-testid="document-preview-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full max-w-5xl h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-[#082c50] text-white">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            <div>
              <div className="text-xs font-bold">{doc.type_label} — {doc.number}</div>
              <div className="text-[10px] opacity-70">{doc.customer_name || "-"}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {blobUrl && (
              <button
                onClick={handleDownload}
                className="p-1.5 hover:bg-white/10 rounded"
                title="Downloaden"
                data-testid="document-preview-download-btn"
              >
                <Download className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/10 rounded"
              title="Sluiten"
              data-testid="document-preview-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden bg-gray-100">
          {loading ? (
            <div
              className="flex items-center justify-center h-full flex-col gap-2"
              data-testid="document-preview-loading"
            >
              <Loader2 className="w-6 h-6 animate-spin text-[#082c50]" />
              <p className="text-sm text-gray-500">Documentpreview wordt geladen…</p>
            </div>
          ) : error ? (
            <div
              className="flex items-center justify-center h-full flex-col gap-2 p-6"
              data-testid="document-preview-error"
            >
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <p className="text-sm text-gray-700 text-center">{error}</p>
            </div>
          ) : isPdf && blobUrl ? (
            <iframe
              src={blobUrl}
              title={filename}
              className="w-full h-full"
              data-testid="document-preview-pdf-iframe"
            />
          ) : isText ? (
            <pre
              className="p-4 h-full overflow-auto font-mono text-xs bg-white"
              data-testid="document-preview-text-content"
            >
              {textContent}
            </pre>
          ) : isXlsx ? (
            <div className="flex items-center justify-center h-full flex-col gap-3 p-6">
              <FileText className="w-10 h-10 text-emerald-600" />
              <p className="text-sm text-gray-700 text-center">
                Excel-bestanden kunnen niet inline getoond worden.<br />
                <span className="text-xs text-gray-500">Gebruik onderstaande knop om te downloaden en te openen.</span>
              </p>
              <button
                onClick={handleDownload}
                className="mt-2 px-4 py-2 bg-emerald-700 text-white text-xs uppercase font-bold tracking-wide hover:bg-emerald-800"
                data-testid="document-preview-xlsx-download"
              >
                <Download className="w-3 h-3 inline mr-1.5" />
                Download {filename}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-gray-500">Onbekend formaat</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
